import { useFormik } from "formik";
import { useEffect, useRef } from "react";
import { FormControl, OverlayTrigger, Table, Tooltip } from "react-bootstrap";
import { BsFillCheckCircleFill, BsXCircle } from "react-icons/bs";
import { constant, modal, table } from "@/utils/constant";
import { emitUpdateTeamParticipantTimezone } from "@/utils/socketHelpers";
import { buildEncryptedUrl } from "../../utils/urlParams";
import { getDepartmentSlug } from "@/utils/department";

function GuestTeamTable({
  socket,
  sessionData,
  teamData,
  current_status,
  current_round,
  roleList,
}) {
  const session = sessionData?.session || {};

  const desiredOrder = [1, 5, 2, 6, 3, 7, 4, 8];
  if (teamData && teamData.participant) {
    const reorderedData = teamData.participant
      .filter((item) => desiredOrder.includes(item.role_id.id))
      .sort(
        (a, b) =>
          desiredOrder.indexOf(a.role_id.id) -
          desiredOrder.indexOf(b.role_id.id)
      );

    teamData.participant = reorderedData;
  }

  const formik = useFormik({
    initialValues: {
      team: teamData?.name,
      team_index: teamData?.team_index,
    },
    onSubmit: async (values) => {
      if (values?.team !== teamData?.name) {
        try {
          socket.emit(
            "admin-team-update",
            {
              data: { name: values?.team },
              session_id: sessionData?.session?.id,
              team_id: teamData?.id,
            },
            (response) => {
              if (!response.success) {
                console.error("Failed to update team:", response.message);
              }
            }
          );
        } catch (error) {
          console.error("Error updating team:", error);
        }
      }
    },
  });

  const handleParticipantJoin = async (item) => {
    if (item?.current_status !== current_status || item?.is_game_leave) {
      const commonValues = {
        participant_id: item?.participant_id?.id,
        role_id: item.role_id?.id,
        team_id: teamData?.id,
        session_id: item?.session_id,
      };

      const newValue = {
        ...commonValues,
        current_status: current_status,
        is_game_leave: false,
        handle_participant_id: [],
      };

      const gameUpdateValue = {
        ...commonValues,
        round_no: current_round,
      };

      socket.emit(
        "admin-join-to-game",
        {
          ...newValue,
          round_no: gameUpdateValue.round_no,
        },
        (response) => {
          if (response.status !== true) {
            console.error("Participant join failed");
          }
        }
      );
    } else {
      const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
      await emitUpdateTeamParticipantTimezone(socket, {
        id: Number(item.id),
        time_zone: timeZone,
      });
    }
  };

  useEffect(() => {
    formik.setFieldValue("team", teamData?.name);
    formik.setFieldValue("team_index", teamData?.team_index);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamData]);

  const gameResetHandledRef = useRef(false);
  useEffect(() => {
    if (!socket || !sessionData?.session?.id) return;
    const onGameReset = (data) => {
      if (
        !data ||
        gameResetHandledRef.current ||
        Number(data.session_id) !== Number(sessionData?.session?.id)
      ) {
        return;
      }
      const teamMatch = data.team_id == null || Number(data.team_id) === Number(teamData?.id);
      if (!teamMatch) return;
      gameResetHandledRef.current = true;
      if (typeof window !== "undefined") {
        window.location.reload();
        localStorage.setItem("modalOpened", true);
      }
    };
    socket.on("game-reset", onGameReset);
    return () => {
      socket.off("game-reset", onGameReset);
    };
  }, [socket, sessionData?.session?.id, teamData?.id]);

  const renderTooltip = (item) => {
    const participantName =
      item?.participant_id?.first_name +
        " " +
        item?.participant_id?.last_name || "";
    return item?.participant_id ? (
      <OverlayTrigger
        placement="bottom"
        overlay={
          <Tooltip id={`tooltip-${participantName}`}>{participantName}</Tooltip>
        }
      >
        <span
          className="overlayTrigger-tooltip text-truncate"
          style={{
            maxWidth: "160px",
            whiteSpace: "nowrap",
          }}
        >
          {item?.current_status > 0 ? (
            !item?.is_game_leave ? (
              <>
                <BsFillCheckCircleFill className="me-2 text-success" />
                {participantName}
              </>
            ) : (
              participantName
            )
          ) : (
            participantName
          )}
        </span>
      </OverlayTrigger>
    ) : (
      <div>{table?.unassigned}</div>
    );
  };
  return (
    <>
      <form>
        <div className="input-group  mb-3 team-input-sec">
          <label className="input-group-text border-0" htmlFor="firstName">
            Team {formik.values.team_index} :
          </label>
          <FormControl
            onBlur={() => formik.handleSubmit()}
            className="border-0"
            id=""
            value={formik.values.team}
            onChange={formik.handleChange}
            name="team"
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                // Just blur - this triggers onBlur which calls handleSubmit once
                event?.target?.blur();
              }
            }}
            placeholder="Add hospital name..."
          />
        </div>
      </form>
      <Table className="mb-3" responsive bordered>
        <thead>
          <tr>
            <th>{constant?.role}</th>
            <th
              style={{ width: "180px", minWidth: "180px", maxWidth: "180px" }}
            >
              {constant?.participant}
            </th>
            <th>{constant?.gameLink}</th>
          </tr>
        </thead>
        <tbody>
          {teamData?.participant?.map((item, index) => {
            return (
              <tr key={index}>
                <td>
                  {roleList?.roles?.find(({ id }) => id === item.role_id?.id)
                    ?.name ?? ""}
                </td>
                <td
                  style={{
                    width: "180px",
                    minWidth: "180px",
                    maxWidth: "180px",
                  }}
                  className={item?.participant_id === null ? "text-danger" : ""}
                >
                  {renderTooltip(item)}
                </td>
                <td>
                  <span>
                    {session?.is_open_game_link === false ? (
                      <BsXCircle />
                    ) : item?.current_status > 0 && !item?.is_game_leave ? (
                      table?.joined
                    ) : item?.participant_id ? (
                      <a
                        target="_blank"
                        className="text-success text-underline cursor-pointer"
                        href={buildEncryptedUrl(
                          `${
                            process.env.REACT_APP_GAME_URL
                          }/${getDepartmentSlug(item?.role_id?.id)}`,
                          {
                            participant_id: item.participant_id?.id,
                            team_id: teamData?.id,
                            session_id: item.session_id,
                            join_as: item.participant_id?.id,
                            department_id: item?.role_id?.id,
                          }
                        )}
                        rel="noreferrer"
                        onClick={() => handleParticipantJoin(item)}
                      >
                        {modal?.joinModalButtonText}
                      </a>
                    ) : (
                      <BsXCircle />
                    )}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </>
  );
}

export default GuestTeamTable;
